import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, Link2, ChevronUp, ChevronDown, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import ReferenceModal from './ReferenceModal';

interface Reference {
  id: string;
  name: string;
  content: string;
  usage: string;
  notes?: string;
  useForEngine: boolean;
  date?: string;
  url?: string;
  created_at: string;
  updated_at: string;
}

const ReferencesView: React.FC = () => {
  const { user } = useAuth();
  const [references, setReferences] = useState<Reference[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentReference, setCurrentReference] = useState<Reference | null>(null);
  const [sortConfig, setSortConfig] = useState<{ column: string; direction: 'asc' | 'desc' } | null>(null);
  const [indexingAll, setIndexingAll] = useState(false);

  const fetchReferences = async () => {

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_references')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setReferences(data || []);
    } catch (error: any) {
      toast.error(`Error fetching references: ${error.message}`);
      setReferences([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
  }, []);

  const indexAllForChat = async () => {
    setIndexingAll(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        indexed?: number;
        skipped?: number;
        chunks?: number;
        error?: string;
      }>('index-content-reference-chunks', { body: { all: true, limit: 500 } });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success(
        `Indexed for chat: ${data?.indexed ?? 0} updated, ${data?.skipped ?? 0} unchanged (${data?.chunks ?? 0} chunks)`,
      );
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Indexing failed');
    } finally {
      setIndexingAll(false);
    }
  };

  const filteredAndSortedReferences = references.filter(reference => {
    const searchLower = searchQuery.toLowerCase();
    return (
      reference.name.toLowerCase().includes(searchLower) ||
      reference.content.toLowerCase().includes(searchLower) ||
      reference.usage.toLowerCase().includes(searchLower)
    );
  }).sort((a, b) => {
    if (!sortConfig) return 0;
    
    const aValue = a[sortConfig.column as keyof Reference];
    const bValue = b[sortConfig.column as keyof Reference];
    
    if (aValue === null) return sortConfig.direction === 'asc' ? -1 : 1;
    if (bValue === null) return sortConfig.direction === 'asc' ? 1 : -1;
    
    return sortConfig.direction === 'asc'
      ? aValue > bValue ? 1 : -1
      : aValue < bValue ? 1 : -1;
  });

  const handleSort = (column: string) => {
    setSortConfig(current => {
      if (current?.column === column) {
        return current.direction === 'asc'
          ? { column, direction: 'desc' }
          : null;
      }
      return { column, direction: 'asc' };
    });
  };

  const handleAddReference = () => {
    setCurrentReference(null);
    setIsModalOpen(true);
  };

  const handleEditReference = (reference: Reference) => {
    setCurrentReference(reference);
    setIsModalOpen(true);
  };

  const getUsageColor = (usage: string) => {
    switch (usage.toLowerCase()) {
      case 'beehiiv': // legacy rows from Beehiiv era
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
      case 'linkedin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'twitter':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-300';
      case 'email':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleUsageClick = (e: React.MouseEvent, reference: Reference) => {
    if (reference.url) {
      e.stopPropagation();
      window.open(reference.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Link2 className="w-8 h-8 mr-3 text-emerald-500" />
            References
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your content references and sources
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search references..."
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={() => void indexAllForChat()}
            disabled={indexingAll}
            className="flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${indexingAll ? 'animate-spin' : ''}`} />
            Index for chat
          </button>
          <button
            onClick={handleAddReference}
            className="flex items-center px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            New Reference
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {['Name', 'Usage', 'Date', 'Engine'].map((header) => (
                  <th
                    key={header}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                    onClick={() => handleSort(header.toLowerCase())}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{header}</span>
                      {sortConfig?.column === header.toLowerCase() && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
                  </td>
                </tr>
              ) : filteredAndSortedReferences.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No references found
                  </td>
                </tr>
              ) : (
                filteredAndSortedReferences.map((reference) => (
                  <tr
                    key={reference.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleEditReference(reference)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {reference.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getUsageColor(reference.usage)} ${reference.url ? 'cursor-pointer hover:opacity-80' : ''}`}
                        onClick={reference.url ? (e) => handleUsageClick(e, reference) : undefined}
                      >
                        {reference.usage}
                        {reference.url && <ExternalLink className="w-3 h-3 ml-1" />}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(reference.date)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${reference.useForEngine ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}></div>
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                          {reference.useForEngine ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditReference(reference);
                        }}
                        className="text-sm text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ReferenceModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        reference={currentReference}
        onSuccess={fetchReferences}
      />
    </div>
  );
};

export default ReferencesView;